import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { apiEnvironment } from "@kells/clinic-one/environments";
import { map, tap } from "rxjs/operators";
import { getFromLocalStorage, saveToLocalStorage } from "@kells/utils/js";

export interface HaleonAuth {
  request_id: string;
  email: string;
  tokens: {
    access_token: string;
    refresh_token: string;
    id_token: string;
    expires_in: number;
  };
}

export interface HaleonAuthResponse {
  message: string;
  data: HaleonAuth;
}

interface StoredAuth {
  auth: HaleonAuth;
  expiresAt: number;
}

const HALEON_AUTH_STORAGE_KEY = "haleon_auth";

@Injectable({
  providedIn: "root",
})
export class HaleonAuthService {
  private readonly haleonAuthApiUrl = `${apiEnvironment.apiBaseUrl}/${apiEnvironment.apiVersion}/haleon/auth`;

  private readonly authSubject = new BehaviorSubject<HaleonAuth | null>(null);

  public readonly auth$ = this.authSubject.asObservable();

  constructor(private http: HttpClient) {
    // Load auth from localStorage on initialization
    this.loadAuthFromStorage();
  }

  /**
   * Gets valid stored auth from localStorage if it exists and hasn't expired.
   * Removes expired tokens from storage.
   * @returns The valid auth or null if expired/missing
   */
  private getValidStoredAuth(): HaleonAuth | null {
    const stored = getFromLocalStorage(HALEON_AUTH_STORAGE_KEY) as
      | StoredAuth
      | undefined;

    if (stored && stored.auth && stored.expiresAt) {
      // Check if token is still valid (with 60 second buffer)
      const now = Date.now();
      if (now < stored.expiresAt - 60000) {
        return stored.auth;
      }
      // Token expired, remove from storage
      localStorage.removeItem(HALEON_AUTH_STORAGE_KEY);
    }

    return null;
  }

  /**
   * Updates the auth subject only if the auth has changed (different request_id or no current auth).
   * This avoids unnecessary emissions when the auth hasn't changed.
   */
  private updateAuthSubjectIfChanged(auth: HaleonAuth): void {
    const currentAuth = this.authSubject.value;
    if (!currentAuth || currentAuth.request_id !== auth.request_id) {
      this.authSubject.next(auth);
    }
  }

  private loadAuthFromStorage(): void {
    const validAuth = this.getValidStoredAuth();
    if (validAuth) {
      this.updateAuthSubjectIfChanged(validAuth);
    }
  }

  private saveAuthToStorage(auth: HaleonAuth): void {
    const expiresAt = Date.now() + auth.tokens.expires_in * 1000;
    const stored: StoredAuth = {
      auth,
      expiresAt,
    };
    saveToLocalStorage(HALEON_AUTH_STORAGE_KEY, stored);
  }

  getAuth(): Observable<HaleonAuth> {
    // Check if we have valid cached auth first
    const validStoredAuth = this.getValidStoredAuth();

    if (validStoredAuth) {
      this.updateAuthSubjectIfChanged(validStoredAuth);
      return of(validStoredAuth);
    }

    return this.http.post<HaleonAuthResponse>(this.haleonAuthApiUrl, null).pipe(
      map((response) => response.data),
      tap((auth) => {
        this.authSubject.next(auth);
        this.saveAuthToStorage(auth);
      })
    );
  }

  resetAuth(): void {
    this.authSubject.next(null);
    localStorage.removeItem(HALEON_AUTH_STORAGE_KEY);
  }
}
